import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Shipment } from 'models/shipments';
import { ShipmentListState } from 'models/Store';
import { findShipmentByIdentifier } from 'utils/shipment.utils';

import { deleteShipments, getShipmentList, queryShipments } from '../thunks';

export const initialState: ShipmentListState = {
  data: null,
};

export const shipmentListSlice = createSlice({
  name: 'shipmentList',
  initialState,
  reducers: {
    updateRecentlySearchedArticles: (state, action: PayloadAction<Shipment[]>) => {
      state.recentlySearched = action.payload;
    },
    clearShipmentList: (state) => {
      state.data = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(deleteShipments.fulfilled, (state, action) => {
      if (!action.payload) return;
      if (!state.removedArticleIds?.length) {
        state.removedArticleIds = action.payload;
      } else {
        state.removedArticleIds = Array.from(new Set([...state.removedArticleIds, ...action.payload]));
      }
      if (state.data) {
        const shipmentEntry = findShipmentByIdentifier(state.data, action.payload);
        if (shipmentEntry)
          state.data = [...state.data.filter((shipment) => shipment.consignmentId !== shipmentEntry.consignmentId)];
      }
      if (state.recentlySearched) {
        const searchedEntry = findShipmentByIdentifier(state.recentlySearched, action.payload);
        if (searchedEntry)
          state.recentlySearched = [
            ...state.recentlySearched.filter((shipment) => shipment.consignmentId !== searchedEntry.consignmentId),
          ];
      }
      state.deleteShipments = null;
    });
    builder.addCase(deleteShipments.rejected, (state) => {
      state.data = null;
    });
    builder.addCase(queryShipments.pending, (state) => {
      state.queryShipments = { loading: true };
    });
    builder.addCase(queryShipments.fulfilled, (state, action) => {
      const newSearchResults = action.payload.successfulResponses;
      if (!state.recentlySearched?.length) state.recentlySearched = newSearchResults;
      const newSearchedIds = new Set(newSearchResults.map((shipment) => shipment.consignmentId));
      const combinedSearchResults = [
        ...state.recentlySearched.filter((searchedResult) => !newSearchedIds.has(searchedResult.consignmentId)),
        ...newSearchResults,
      ];
      if (state.removedArticleIds?.length) {
        state.removedArticleIds = [...state.removedArticleIds].filter((id) => !newSearchedIds.has(id));
      }
      state.recentlySearched = combinedSearchResults;
      state.queryShipments = null;
    });
    builder.addCase(queryShipments.rejected, (state, action) => {
      state.data = null;
      state.queryShipments = {
        loading: false,
        error: action.payload ?? action.error,
      };
    });
    builder.addCase(getShipmentList.pending, (state) => {
      state.getShipmentList = { loading: true };
    });
    builder.addCase(getShipmentList.fulfilled, (state, action) => {
      state.data = action.payload;
      state.getShipmentList = null;
    });
    builder.addCase(getShipmentList.rejected, (state, action) => {
      state.data = null;
      state.getShipmentList = {
        loading: false,
        error: action.payload ?? action.error,
      };
    });
  },
});

export const { updateRecentlySearchedArticles, clearShipmentList } = shipmentListSlice.actions;
